import { Error } from '@dehaat/kisan-app-bl/models/Order';
import User from '@dehaat/kisan-app-bl/models/User';
import { FULFILLMENT_TYPE, Vendor } from '@dehaat/kisan-app-bl/models/Vendor';
import { AxiosError } from 'axios';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { createContext, Dispatch, PropsWithChildren, SetStateAction, useCallback, useContext, useEffect, useState } from 'react';
import ErrorModal from '@/components/ErrorModal';
import Okay from '@/components/modalActions/Okay';
import { CART_ERRORS, CART_TYPE } from '@/constants/cart';
import { AVAILABLE_COMBO_STOCK, AVAILABLE_STOCK, COOKIE_MAX_AGE_1YEAR, CURRENT_VENDOR_KEY, INSURANCE_KEY, IS_PREBOOK_FLOW, MY_CART, ONDC_VENDOR_KEY, ONE_CLICK_APP_SOURCE, OUT_OF_STOCK_COMBOS, OUT_OF_STOCK_PRODUCTS, VENDOR_ID_COOKIE_NAME } from '@/constants/common';
import { allServicesProductVariantIds } from '@/constants/shriProgram';
import { useAppSelector } from '@/hooks/reduxHooks';
import useErrorModalState from '@/hooks/useErrorModalState';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import { ActiveDiscount } from '@/models/ActiveDiscount';
import { DELIVERY_TYPE } from '@/models/Cart';
import { CartData, CartState, CartStateInStorage, PriceInStorage, VendorMismatchAdditionalInfo } from '@/models/Product';
import { selectCurrentLocation, selectOndcVendor, selectVendorsList, setCurrentVendor } from '@/store/features/locationSlice';
import { captureCartEvent } from '@/utils/analytics';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
import { CaptureUpdateCartEvent
// trackCustomEvent,
} from '@/utils/analytics/googleAnalytics';
import { ProductListTypeEnum } from '@/utils/analytics/types';
import { isShareChatUtmSession } from '@/utils/analytics/utmParams';
import createCart from '@/utils/api/createCart';
import deleteCart from '@/utils/api/deleteCart';
import { fetchCartDetailsById, isComboOrder } from '@/utils/api/fetchCartDetailsById';
import { isCartEmpty, isOnlyServicesInCart, isPrebookExitRoute, isShriCardProductInCart, isShriProgramProduct, updateCartIdInLocalStorage, updateCartInLocalStorage } from '@/utils/cart';
import { getLocalStorageKey, isSessionStorageAvailable, removeLocalStorageKey, setLocalStorageKey } from '@/utils/helper';
import { AuthContext } from './AuthProvider';
interface CartUpdateParams {
  variantId: string;
  quantity: number;
  index?: number;
  type?: ProductListTypeEnum;
  listName?: string;
  variantPrice: number;
  variantDiscount: number;
  activeDiscount?: ActiveDiscount | null;
  name?: string;
  brandName?: string;
  catalogueId: number;
  groupId: object;
  isCombo?: boolean;
  parentType?: string;
}
interface Context {
  cartId: string;
  cartItems: Record<string, number>;
  comboItems: Record<string, number>;
  currentVendor: Vendor | null;
  currentVendorId: string | null;
  pickupFarmerDetails?: User;
  resetCart: (items?: Record<string, number>, keepShriProduct?: boolean, skipDeleteCart?: boolean, params?: CartUpdateParams, catalogueIds?: Record<string, number>) => void;
  setPickupFarmerDetails: (farmer: User | undefined) => void;
  updateCart: (params: CartUpdateParams, isAddToCart: boolean) => void;
  validateVendor: (availableVendorId: string | null, isAddToCart?: boolean, params?: CartUpdateParams) => boolean;
  updateVendorDetails: (vendor: Vendor | null) => void;
  vendorId: string | null;
  cartType: CART_TYPE;
  isPrebookFlow: boolean;
  isPickupOrder: boolean;
  deliveryType?: DELIVERY_TYPE;
  isCartLoading: boolean;
  setDeliveryType: Dispatch<SetStateAction<DELIVERY_TYPE | undefined>>;
  updateCartState: (key: keyof CartData, value: unknown) => void;
}
export const CartContext = createContext<Context>({
  cartId: '',
  cartItems: {},
  currentVendor: null,
  currentVendorId: null,
  pickupFarmerDetails: undefined,
  resetCart: () => undefined,
  setPickupFarmerDetails: () => undefined,
  updateCart: () => undefined,
  validateVendor: () => false,
  updateVendorDetails: () => undefined,
  vendorId: null,
  isPrebookFlow: false,
  cartType: CART_TYPE.DEHAAT,
  isPickupOrder: false,
  deliveryType: DELIVERY_TYPE.HOME_DELIVERY,
  isCartLoading: false,
  setDeliveryType: () => {
    /**/
  },
  updateCartState: () => undefined,
  comboItems: {}
});
const emptyCartData: CartData = {
  cartId: '',
  cartItems: {},
  currentVendorId: null,
  currentVendor: null,
  pickupFarmerDetails: undefined,
  vendorId: null,
  catalogueIds: {},
  groupIds: {},
  comboItems: {}
};
export const cartInitialState = {
  [CART_TYPE.DEHAAT]: emptyCartData,
  [CART_TYPE.ONDC]: emptyCartData
};
const CartProvider = ({
  children
}: PropsWithChildren<Record<never, never>>) => {
  const {
    asPath,
    query,
    pathname,
    reload
  } = useRouter();
  const currentLocation = useAppSelector(selectCurrentLocation);
  // Re-read the saved ONDC vendor once it is fetched on entering /ondc
  const ondcVendor = useAppSelector(selectOndcVendor);
  const {
    isAuthenticated
  } = useContext(AuthContext);
  const cartType = asPath?.includes('ondc') ? CART_TYPE.ONDC : CART_TYPE.DEHAAT;
  const [cartState, setCartState] = useState<CartState>(cartInitialState);
  const [isPrebookFlow, setIsPrebookFlow] = useState(false);
  const [isCartLoading, setIsCartLoading] = useState(false);
  const {
    cartId,
    cartItems,
    currentVendorId,
    currentVendor,
    pickupFarmerDetails,
    vendorId,
    comboItems
  } = cartState[cartType];
  const [deliveryType, setDeliveryType] = useState<DELIVERY_TYPE | undefined>(DELIVERY_TYPE.HOME_DELIVERY);
  const {
    t
  } = useTranslation('common');
  const {
    errorModalState,
    setErrorModalState,
    hideErrorModal
  } = useErrorModalState();
  const {
    trackCustomEvent
  } = useTriggerEvents();
  const vendorList = useAppSelector(selectVendorsList);
  const updateCartState = useCallback((key: keyof CartData, value: unknown) => {
    setCartState(prev => ({
      ...prev,
      [cartType]: {
        ...prev[cartType],
        [key]: value
      }
    }));
  }, [cartType]);
  const updateVendorDetails = useCallback((vendor: Vendor | null) => {
    if (isSessionStorageAvailable(window)) {
      sessionStorage.removeItem(AVAILABLE_STOCK);
      sessionStorage.removeItem(AVAILABLE_COMBO_STOCK);
      sessionStorage.removeItem(OUT_OF_STOCK_PRODUCTS);
      sessionStorage.removeItem(OUT_OF_STOCK_COMBOS);
    }
    setCartState(prev => {
      const dataWithVendor = {
        ...prev[cartType],
        currentVendorId: vendor ? vendor.id.toString() : null,
        currentVendor: vendor,
        vendorFulfillmentType: vendor?.fulfillment_type
      };
      return {
        ...prev,
        [cartType]: dataWithVendor
      };
    });
  }, [cartType]);
  const setPickupFarmerDetails = useCallback((user: User | undefined) => updateCartState('pickupFarmerDetails', user), [updateCartState]);
  const updateCartToBE = useCallback(async (params: CartUpdateParams, vendor: string, newQuantity: number, cartId?: string, retryCount = 1, shouldOverrideCart: boolean = false) => {
    const pricingData: PriceInStorage = getLocalStorageKey('pp_d', {}) || {};
    const existingPricing = pricingData[params.variantId];
    if (newQuantity <= 0) {
      delete pricingData[params.variantId];
    } else {
      pricingData[params.variantId] = {
        price: params.variantPrice,
        discount: params.variantDiscount,
        active_discount: params.activeDiscount !== undefined ? params.activeDiscount : existingPricing?.active_discount ?? null
      };
    }
    setLocalStorageKey('pp_d', pricingData);
    if (!isAuthenticated) {
      return;
    }
    if (pathname === '/cart') {
      return;
    }
    const {
      variantId,
      catalogueId,
      variantPrice,
      variantDiscount,
      isCombo,
      parentType
    } = params;
    const product_details = isCombo ? [{
      price: variantPrice,
      discount: variantDiscount,
      quantity: newQuantity,
      item_variant: Number(variantId),
      parent_type: parentType
    }] : [{
      c_id: catalogueId,
      price: variantPrice,
      discount: variantDiscount,
      quantity: newQuantity,
      product_variant: Number(variantId)
    }];
    try {
      const res = await createCart({
        vendor,
        product_details,
        is_login_request: shouldOverrideCart,
        cart_id: cartId || ''
      });
      if (res?.cart_id && res?.cart_id !== cartId) {
        setCartState(prev => {
          return {
            ...prev,
            [cartType]: {
              ...prev[cartType],
              cartId: res?.cart_id
            }
          };
        });
        updateCartIdInLocalStorage(res.cart_id);
      }
    } catch (e: any) {
      let error = e?.data?.error;
      let errorCode = error?.code;
      if (e.error) {
        error = e.error;
        errorCode = error?.code;
      }
      const shouldRetry = (errorCode === CART_ERRORS.CART_401 || errorCode === CART_ERRORS.CART_404) && retryCount === 1;
      if (shouldRetry) {
        return updateCartToBE(params, vendor, newQuantity, '', 0);
      }
    }
  }, [cartType, isAuthenticated]);
  const addToCart = useCallback((params: CartUpdateParams) => {
    if (typeof window !== 'undefined' && window.scq && isShareChatUtmSession()) {
      window.scq('Add to cart', 'pre_defined');
    }
    const {
      variantId,
      quantity,
      catalogueId,
      groupId,
      isCombo
    } = params;
    captureCartEvent({
      eventType: 'add_to_cart',
      ...params
    });
    setCartState(prev => {
      let newQuantity = 0;
      if (isCombo) {
        newQuantity = (prev[cartType].comboItems[variantId] || 0) + quantity;
      } else {
        newQuantity = (prev[cartType].cartItems[variantId] || 0) + quantity;
      }
      const vendor = prev[cartType].currentVendorId || '';
      updateCartToBE(params, vendor, newQuantity, prev[cartType].cartId || '');
      const updatedComboItems = {
        ...prev[cartType].comboItems
      };
      const updatedItems = {
        ...prev[cartType].cartItems
      };
      const updatedcatalogueIds = {
        ...prev[cartType].catalogueIds
      };
      const updateGroupIds = {
        ...prev[cartType].groupIds
      };
      if (isCombo) {
        const newQuantity = (prev[cartType].comboItems[variantId] || 0) + quantity;
        updatedComboItems[variantId] = newQuantity;
      } else {
        const newQuantity = (prev[cartType].cartItems[variantId] || 0) + quantity;
        updatedItems[variantId] = newQuantity;
        if (catalogueId) {
          updatedcatalogueIds[variantId] = catalogueId;
        }
        if (groupId) {
          updateGroupIds[variantId] = groupId;
        }
      }
      updateCartInLocalStorage(cartType, updatedItems, null, '', !allServicesProductVariantIds.includes(variantId) ? isPrebookFlow : undefined, updatedcatalogueIds, updateGroupIds, updatedComboItems);
      return {
        ...prev,
        [cartType]: {
          ...prev[cartType],
          cartItems: updatedItems,
          catalogueIds: updatedcatalogueIds,
          groupIds: updateGroupIds,
          comboItems: updatedComboItems
        }
      };
    });
  }, [cartType, isPrebookFlow, updateCartToBE]);
  const removeFromCart = useCallback((params: CartUpdateParams) => {
    const {
      variantId,
      quantity,
      isCombo
    } = params;
    CaptureUpdateCartEvent({
      eventType: 'remove_from_cart',
      ...params
    });
    setCartState(prev => {
      if (prev[cartType].cartItems[variantId] || prev[cartType].comboItems[variantId]) {
        const updatedComboItems = {
          ...prev[cartType].comboItems
        };
        let updatedItems = {
          ...prev[cartType].cartItems
        };
        let cartId = prev[cartType].cartId;
        const updatedCatalogueIds = {
          ...prev[cartType].catalogueIds
        };
        const updateGroupIds = {
          ...prev[cartType].groupIds
        };
        if (isCombo) {
          const newQuantity = (prev[cartType].comboItems[variantId] || 0) - quantity;
          if (newQuantity > 0) {
            updatedComboItems[variantId] = newQuantity;
          } else {
            delete updatedComboItems[variantId];
          }
        } else {}
        const newQuantity = prev[cartType].cartItems[variantId] - quantity;
        const vendor = prev[cartType].currentVendorId || '';
        if (newQuantity > 0) {
          updatedItems = {
            ...prev[cartType].cartItems,
            [variantId]: newQuantity
          };
        } else {
          delete updatedItems[variantId];
          delete updatedCatalogueIds[variantId];
          delete updateGroupIds[variantId];
        }
        if (Object.keys(updatedItems).length !== 0) {
          let newQty = newQuantity || 0;
          if (isCombo) {
            newQty = (prev[cartType].comboItems[variantId] || 0) - quantity;
          }
          updateCartToBE(params, vendor, newQty, cartId || '');
        }
        updateCartInLocalStorage(cartType, updatedItems, null, '', isOnlyServicesInCart(updatedItems) ? false : isPrebookFlow, updatedCatalogueIds, updateGroupIds, updatedComboItems);
        if (Object.keys({
          ...updatedItems,
          ...updatedComboItems
        }).length === 0) {
          if (cartId) {
            deleteCart(cartId);
            updateCartIdInLocalStorage('');
            cartId = '';
          }
        }
        return {
          ...prev,
          [cartType]: {
            ...prev[cartType],
            cartItems: updatedItems,
            catalogueIds: updatedCatalogueIds,
            groupIds: updateGroupIds,
            cartId: cartId,
            comboItems: updatedComboItems
          }
        };
      }
      return prev;
    });
  }, [cartType, isPrebookFlow, updateCartToBE]);

  // keep services in cart when cart.isPrebook is changed and remove other products
  // else clear the cart
  const resetCart = useCallback((items?: Record<string, number>, removeServicesInCart = true, skipDeleteCart = false, params?: CartUpdateParams, catalogueIds?: Record<string, number>) => {
    const uuid = ''; //createUUID()
    let updatedItems = items || {};
    if (!removeServicesInCart && isShriCardProductInCart(cartItems, true)) {
      const itemsInCart = Object.keys(cartItems);
      const servicesInCard = itemsInCart.filter(id => isShriProgramProduct(id, true)) as string[];
      const serviceItems = servicesInCard.reduce((acc: Record<string, number>, productId) => {
        acc[productId] = cartItems[productId];
        return acc;
      }, {});
      updatedItems = {
        ...serviceItems,
        ...items
      };
    }
    if (cartId && !skipDeleteCart) {
      deleteCart(cartId);
      updateCartIdInLocalStorage('');
    }
    if (Object.keys(updatedItems).length !== 0 && params && currentVendorId) {
      updateCartToBE(params, currentVendorId, params.quantity, cartId || '', 1, skipDeleteCart);
    }
    updateCartInLocalStorage(cartType, updatedItems, currentVendorId, uuid, isPrebookFlow, catalogueIds, {}, {});
    removeLocalStorageKey(INSURANCE_KEY);
    removeLocalStorageKey('pp_d');
    setCartState(prev => {
      const {
        currentVendorId
      } = prev[cartType];
      const newData = {
        ...prev[cartType],
        cartId: uuid,
        vendorId: currentVendorId,
        cartItems: updatedItems || {},
        catalogueIds: catalogueIds || {},
        groupIds: {},
        comboItems: {}
      };
      return {
        ...prev,
        [cartType]: newData
      };
    });
  }, [cartId, cartItems, cartType, currentVendorId, isPrebookFlow, updateCartToBE]);
  const onCartReset = useCallback((params?: CartUpdateParams, removeServicesInCart = true) => {
    trackCustomEvent(CustomEventTypes.CART_RESET);
    const items = params != null ? {
      [params.variantId]: params.quantity
    } : {};
    const cIds = params != null ? {
      [params.variantId]: params.catalogueId
    } : {};
    if (params) {
      captureCartEvent({
        eventType: 'add_to_cart',
        ...params
      });
    }
    resetCart(items, removeServicesInCart, true, params, cIds);
    if (isSessionStorageAvailable(window)) {
      sessionStorage.removeItem(AVAILABLE_STOCK);
      sessionStorage.removeItem(AVAILABLE_COMBO_STOCK);
      sessionStorage.removeItem(OUT_OF_STOCK_PRODUCTS);
      sessionStorage.removeItem(OUT_OF_STOCK_COMBOS);
    }
    hideErrorModal();
  }, [hideErrorModal, resetCart, trackCustomEvent]);
  const validateVendor = useCallback((availableVendorId: string | null, isAddToCart = false, params?: CartUpdateParams) => {
    if (vendorId !== availableVendorId) {
      setErrorModalState({
        allowClose: true,
        message: t('vendor_changed_msg'),
        show: true,
        additionalInfo: isAddToCart ? params : undefined
      });
      return false;
    }
    return true;
  }, [setErrorModalState, t, vendorId]);

  // it will only validate flow if user is not on the cart page.
  const validateFlow = useCallback((isAddToCart = false, params?: CartUpdateParams) => {
    const cartData = getLocalStorageKey<CartStateInStorage, null>(MY_CART, null);
    if (cartData && isOnlyServicesInCart(cartData[cartType].items)) {
      return true;
    }

    // if in prebook flow and user tries to add normal booking product
    // or vice versa, show error modal
    if (cartData && isPrebookFlow !== Boolean(cartData[cartType].isPrebook) && Object.keys(cartData[cartType].items).length > 0 && asPath !== '/cart') {
      setErrorModalState({
        allowClose: true,
        message: t('order_type_changed_msg', {
          orderType: cartData[cartType].isPrebook ? t('prebook') : t('normal_booking')
        }),
        show: true,
        additionalInfo: isAddToCart ? params : undefined,
        removeServicesInCart: false
      });
      return false;
    }
    return true;
  }, [cartType, isPrebookFlow, asPath, setErrorModalState, t]);
  const updateCart = useCallback((params: CartUpdateParams, isAddToCart: boolean) => {
    if (validateVendor(currentVendorId, isAddToCart, params) && validateFlow(isAddToCart, params)) {
      return isAddToCart ? addToCart(params) : removeFromCart(params);
    }
  }, [addToCart, removeFromCart, validateVendor, currentVendorId, validateFlow]);
  const updateCartAndVendor = useCallback(({
    cartItems,
    cartId,
    catalogueIds,
    groupIds,
    vendor,
    comboItems
  }: {
    cartItems: Record<string, number>;
    cartId: string;
    catalogueIds: Record<string, number>;
    groupIds?: Record<string, object>;
    vendor: Vendor;
    comboItems: Record<string, number>;
  }) => {
    const newCart = updateCartInLocalStorage(CART_TYPE.DEHAAT, cartItems, vendor.id.toString(), cartId, false, catalogueIds, groupIds, comboItems);
    const appCode = query.app_code as string;
    updateVendorDetails(vendor);
    setCurrentVendor(vendor);
    document.cookie = `${VENDOR_ID_COOKIE_NAME}=${vendor.id}; max-age=${COOKIE_MAX_AGE_1YEAR}; path=/`;
    setLocalStorageKey(CURRENT_VENDOR_KEY, vendor || null);
    appCode && sessionStorage.setItem(ONE_CLICK_APP_SOURCE, appCode);
    setCartState(prev => {
      const newData = {
        ...prev[cartType],
        cartId: newCart[cartType].cartId,
        vendorId: newCart[cartType].vendorId || null,
        cartItems: newCart[cartType].items as Record<string, number>,
        catalogueIds: newCart[cartType].catalogueIds,
        groupIds: newCart[cartType].groupIds,
        comboItems: newCart[cartType].comboItems || {}
      };
      return {
        ...prev,
        [cartType]: newData
      };
    });
    setErrorModalState({
      show: false,
      allowClose: true,
      message: '',
      additionalInfo: undefined
    });
  }, [cartType, query.app_code, setErrorModalState, updateVendorDetails]);
  const updateCartById = useCallback(async (cartId: string) => {
    if (!cartId) return;
    setIsCartLoading(true);
    try {
      const cartItems: Record<string, number> = {};
      const catalogueIds: Record<string, number> = {};
      const groupIds: Record<string, object> = {};
      const comboItems: Record<string, number> = {};
      const response = await fetchCartDetailsById(cartId);
      const apiVendorId = response.vendor;
      response.order_details.forEach(detail => {
        if (isComboOrder(detail)) {
          const variantId = detail.item_variant.toString();
          comboItems[variantId] = detail.quantity;
        } else {
          const variantId = detail.product_variant.toString();
          cartItems[variantId] = detail.quantity;
          catalogueIds[variantId] = detail.c_id;
        }
      });
      const availableVendor: Vendor | undefined = vendorList.find(vendor => vendor.id === apiVendorId);
      setIsCartLoading(false);
      if (!availableVendor) {
        return;
      }
      if (vendorId && vendorId !== apiVendorId.toString()) {
        const additionalInfo: VendorMismatchAdditionalInfo = {
          type: 'BE_vendor_mismatch',
          vendorId: apiVendorId,
          cartId,
          cartItems,
          catalogueIds,
          groupIds,
          vendor: availableVendor,
          comboItems
        };
        setErrorModalState({
          allowClose: true,
          show: true,
          message: t('common:vendor_changed_msg'),
          additionalInfo
        });
        return;
      }
      updateCartAndVendor({
        cartItems,
        cartId,
        catalogueIds,
        groupIds,
        vendor: availableVendor,
        comboItems
      });
    } catch (error: any) {
      setIsCartLoading(false);
      console.log('Error fetching cart details:', error);

      // setErrorModalState({
      //   show: true,
      //   message:
      //     error.response.data.error ||
      //     error?.message ||
      //     'Failed to fetch cart details. Please try again later.',
      // })
    }
  }, [setErrorModalState, t, updateCartAndVendor, vendorId, vendorList]);

  // Set Delivery type on change on Current Vendor
  useEffect(() => {
    const type = currentVendor?.fulfillment_type !== FULFILLMENT_TYPE.PICKUP_FROM_STORE ? DELIVERY_TYPE.HOME_DELIVERY : DELIVERY_TYPE.SELF_PICKUP;
    setDeliveryType(type);
  }, [currentVendor?.fulfillment_type]);

  // Updates the current vendor if the cart is empty or not initialized
  useEffect(() => {
    const cartData = getLocalStorageKey<CartStateInStorage, null>(MY_CART, null);
    if (cartData == null || Object.keys(cartData[cartType].items).length === 0) {
      const uuid = ''; //createUUID()
      updateCartInLocalStorage(cartType, {}, currentVendorId, uuid, isPrebookFlow, {});
      setCartState(prev => {
        const {
          currentVendorId
        } = prev[cartType];
        const newData = {
          ...prev[cartType],
          cartId: uuid,
          vendorId: currentVendorId
        };
        return {
          ...prev,
          [cartType]: newData
        };
      });
    }
  }, [currentVendorId, asPath, cartType, isPrebookFlow]);

  // Set cart items from local storage
  useEffect(() => {
    const cart_id = query?.cid ? query.cid as string : null;
    if (pathname === '/cart' && cart_id && vendorList.length) {
      updateCartById(cart_id);
    } else {
      const cartData = getLocalStorageKey<CartStateInStorage, null>(MY_CART, null);
      if (cartData != null) {
        setCartState(prev => {
          const newData = {
            ...prev[cartType],
            cartId: cartData[cartType].cartId,
            vendorId: cartData[cartType].vendorId || null,
            cartItems: cartData[cartType].items as Record<string, number>,
            catalogueIds: cartData[cartType].catalogueIds,
            groupIds: cartData[cartType].groupIds,
            comboItems: cartData[cartType].comboItems || {}
          };
          return {
            ...prev,
            [cartType]: newData
          };
        });
      }
    }
  }, [asPath, cartType, pathname, query.cid, updateCartById, vendorList.length]);

  // Reset error modal state on route change
  useEffect(() => {
    const savedVendor = getLocalStorageKey<Vendor, null>(cartType === CART_TYPE.DEHAAT ? CURRENT_VENDOR_KEY : ONDC_VENDOR_KEY) || null;
    updateVendorDetails(savedVendor);
  }, [asPath, cartType, updateVendorDetails, currentLocation, ondcVendor]);
  useEffect(() => {
    setErrorModalState(prev => prev.show ? {
      ...prev,
      show: false
    } : prev);
  }, [asPath, setErrorModalState]);
  useEffect(() => {
    if (query.prebook == 'true') {
      setLocalStorageKey(IS_PREBOOK_FLOW, true);
      setIsPrebookFlow(true);
    } else {
      const isPrebookFlow = getLocalStorageKey(IS_PREBOOK_FLOW);
      if (isPrebookFlow && isPrebookExitRoute(pathname)) {
        setLocalStorageKey(IS_PREBOOK_FLOW, false);
        setIsPrebookFlow(false);
      } else {
        setIsPrebookFlow(Boolean(isPrebookFlow));
      }
    }
  }, [pathname, query?.prebook]);
  useEffect(() => {
    const isPrebook = getLocalStorageKey(IS_PREBOOK_FLOW);
    const cartData = getLocalStorageKey<CartStateInStorage, null>(MY_CART) || null;
    if (cartData && (isCartEmpty(cartData[cartType].items) || isOnlyServicesInCart(cartData[cartType].items))) {
      setLocalStorageKey<CartStateInStorage>(MY_CART, {
        ...cartData,
        [cartType]: {
          ...cartData[cartType],
          isPrebook: Boolean(isPrebook)
        }
      });
    }
  }, [asPath, cartType]);
  return <CartContext.Provider value={{
    cartId,
    cartItems,
    comboItems,
    currentVendor,
    currentVendorId,
    pickupFarmerDetails,
    resetCart,
    setPickupFarmerDetails,
    updateCart,
    validateVendor,
    updateVendorDetails,
    vendorId,
    isPrebookFlow,
    cartType,
    deliveryType,
    setDeliveryType,
    isPickupOrder: deliveryType === DELIVERY_TYPE.SELF_PICKUP,
    isCartLoading,
    updateCartState
  }} data-sentry-element="unknown" data-sentry-component="CartProvider" data-sentry-source-file="CartProvider.tsx">
      {children}
      <ErrorModal {...errorModalState} onClose={hideErrorModal} data-sentry-element="ErrorModal" data-sentry-source-file="CartProvider.tsx">
        <Okay handleClick={() => {
        const additionalInfo = errorModalState.additionalInfo as VendorMismatchAdditionalInfo | undefined;
        if (additionalInfo && additionalInfo.type && additionalInfo.type === 'BE_vendor_mismatch') {
          if (cartId) deleteCart(cartId);
          const {
            cartItems,
            cartId: cId,
            catalogueIds,
            groupIds,
            vendor,
            comboItems
          } = additionalInfo;
          updateCartAndVendor({
            cartItems,
            cartId: cId,
            catalogueIds,
            groupIds,
            vendor,
            comboItems
          });
        } else {
          onCartReset(errorModalState.additionalInfo as CartUpdateParams | undefined, errorModalState?.removeServicesInCart);
        }
      }} data-sentry-element="Okay" data-sentry-source-file="CartProvider.tsx" />
      </ErrorModal>
    </CartContext.Provider>;
};
export default CartProvider;